import clsx from "clsx";
import "./FilterDropDown.scss";
import { ArrowDown } from "@/icons/ArrowDown";
import { useEffect, useRef, useState } from "react";
import { ArrowList } from "@/icons/ArrowList";
import { Col } from "@/components";
import { useFocusTrap } from "@/hooks/useFocusTrap";

export type FilterDropDownItem = {
  label: string;
  value: string;
};

type FilterDropdownProps = {
  size: "small" | "large";
  items: FilterDropDownItem[];
  className?: string;
  filter: string;
  setFilter: (filter: string) => void;
};
export const FilterDropDown = ({
  size,
  items = [],
  className,
  filter,
  setFilter,
}: FilterDropdownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useFocusTrap(listRef, isExpanded);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsExpanded(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isExpanded]);

  return (
    <div
      ref={dropdownRef}
      style={size === "large" ? { width: "668px" } : { width: "322px" }}
      className={clsx(className, "ui-filter-dropdown")}
    >
      <button
        className="ui-button-accordion"
        type="button"
        aria-expanded={isExpanded}
        aria-haspopup="listbox"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h1 className="fw-semibold fs-custom text-primary">{filter}</h1>
        <ArrowDown
          color="primary"
          className={clsx("ui-arrow", { "ui-expanded": isExpanded })}
        />
      </button>
      {isExpanded && (
        <div
          ref={listRef}
          className={"ui-wrap-list-item-filter position-relative pt-x12"}
        >
          <ArrowList className={"ui-arrow-list position-absolute"} />
          <div className={"p-x24 h-full shadow"}>
            <Col
              gap={"x24"}
              text={"primary"}
              fs={"custom"}
              fw={"regular"}
              className={"ui-list-item-filter h-full overflow fw-regular"}
              role="listbox"
            >
              {items.map((item, index) => {
                return (
                  <div
                    key={index}
                    role="option"
                    aria-selected={item.value === filter}
                    tabIndex={0}
                    onClick={() => {
                      setFilter(item.value);
                      setIsExpanded(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setFilter(item.value);
                        setIsExpanded(false);
                      }
                    }}
                  >
                    {item.label}
                  </div>
                );
              })}
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};
